import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap, take, filter } from 'rxjs/operators';

import { AppConfigService } from '@alfresco/adf-core';
import { Store } from '@ngrx/store';

import {
  SemanticSearchRequestBody,
  SemanticSearchResponse,
} from '../models/semantic-search.model';
import { WkAuthenticationService } from './wk-authentication.service';
import { WINDOW_TOKEN } from '../components/shared/window.token';
import { AppStore, currentSiteSelector } from '../store';

@Injectable({
  providedIn: 'root',
})
export class WkSemanticSearchService {
  private readonly DEFAULT_ROWS = 50;
  private readonly DEFAULT_RETURN_FIELDS = [
    'wkdocid',
    'payload',
    'tenantid',
    'title',
  ];

  constructor(
    private readonly http: HttpClient,
    private readonly appConfigService: AppConfigService,
    private readonly authService: WkAuthenticationService,
    private readonly store: Store<AppStore>,
    @Inject(WINDOW_TOKEN) private readonly window: Window
  ) {}

  public getDocumentPreviewUrl(tenantId: string): void {
    const ecmHost = this.appConfigService.get<string>('ecmHost', '');
    let url = `${ecmHost}/alfresco/s/wk/document/preview?tenantId=${encodeURIComponent(
      tenantId
    )}`;

    if (!this.authService.isSsoLoggedIn()) {
      url = `${url}&alf_ticket=${this.authService.getTicketEcm()}`;
    }
    this.window.open(url, '_blank');
  }

  public search(
    query: string,
    start = 0,
    rows?: number
  ): Observable<SemanticSearchResponse> {
    const configuredRows = rows ?? this.DEFAULT_ROWS;
    const body = this.buildRequestBody(query, start, configuredRows);
    return this.store.select(currentSiteSelector).pipe(
      filter((currentSite) => !!currentSite),
      take(1),
      switchMap((currentSite) => {
        const url = this.getApiUrl(currentSite);
        return this.http.post<SemanticSearchResponse>(url, body, {
          headers: this.getHeaders(),
        });
      })
    );
  }

  private buildRequestBody(
    query: string,
    start: number,
    rows: number
  ): SemanticSearchRequestBody {
    return {
      input: {
        searchRequest: {
          searchType: 'vector',
          queryString: query,
          enhancedQueryString: '*',
          queryStrings: [],
          query: {
            filterSpecs: [],
            resultSpec: {
              rows,
              start,
              returnFields: this.DEFAULT_RETURN_FIELDS,
              sort: [],
              resultFormat: 'flat',
              limit: 2048,
              threshold: 0,
              includeStats: [],
              categoryFilters: [],
            },
            textQueryOperator: 'and',
          },
          collapse: {
            field: null,
            maxPerGroup: null,
            sort: [],
          },
        },
        searchConnectorRequestParams: {
          shiftId: null,
          namespace: null,
          productId: 'default',
          requestType: 'search',
        },
      },
    };
  }

  private getApiUrl(currentSite: string | undefined): string {
    const ecmHost = this.appConfigService.get<string>('ecmHost', '');
    return `${ecmHost}/alfresco/s/wk/core/ai/apollo_semantic_search/${currentSite?.replace(
      '-',
      '_'
    )}/`;
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    if (!this.authService.isSsoLoggedIn()) {
      const token = this.authService.getContentClientBasicAuthToken();
      headers = headers.set('Authorization', `Basic ${token}`);
    }

    return headers;
  }
}
